<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'

import BoostDrawer from './BoostDrawer.vue'
import UserFormDrawer from './UserFormDrawer.vue'
import IntroDrawer from './IntroDrawer.vue'
import ContestGame from './index.vue'
import type {
  ContestColor,
  CustomerForm,
  ErrorResponse,
  FormattedContestGameReward,
  GameContest,
  Merchant,
} from './contestGame'
import type { ClientApi } from '@/api/api'

const props = defineProps<{
  clientApi: ClientApi
  useLocalStorageAvailability: boolean
  contestColor: ContestColor
  contestGame: GameContest
  contestGameRewards: FormattedContestGameReward[]
  contestReward: FormattedContestGameReward
  id: string
  locale: string
  logo: string
  storeSlug: string
  webAppUrl: string
  setContestReward: (reward: FormattedContestGameReward) => void
}>()

const merchant = computed<Merchant>(() => props.contestGame.merchant)
const redirectUrl = computed(
  () =>
    props.contestGame?.stores?.find((store) => store?.store?.slug === props.storeSlug)
      ?.trackedTargetUrl,
)

const isContestLoaded = ref(true)
const launchGame = ref(false)
const hasSeenBoostModal = ref(false)
const apiError = ref({
  phoneNumber: false,
  email: false,
})
const stepper = reactive({
  intro: true,
  boost: false,
  form: false,
})
const loading = ref(false)
const errorModal = ref(false)
const errorText = ref<ErrorResponse>({
  data: {
    message: '',
  },
})
const boostStep = ref(1)
const userForm = ref<CustomerForm>({
  phoneNumber: '',
  countryCode: '',
  email: '',
  firstName: '',
  hasOptin: false,
})
const isUserFormSubmitLoading = ref(false)
const userFormError = ref('')

const appUrl = computed(() => {
  let url = `https://${props.webAppUrl}`
  if (userForm.value.phoneNumber) {
    url += `?phoneNumber=${encodeURIComponent(userForm.value.phoneNumber)}`
  }

  return url
})

const showBoostModal = () => {
  const localStorageKey = `hasParticipated-${props.contestGame.id}-${props.contestGame.stores[0].storeId}-${props.contestGame.contestGameBoostType.slug}`
  const hasSeenBoost =
    localStorage.getItem(localStorageKey) ||
    localStorage.getItem(`hasParticipated-${props.contestGame.id}`)
  const isBoostDisabled =
    props.contestGame.contestGameBoostType && props.contestGame.contestGameBoostType.slug === 'none'
  if (isBoostDisabled || !!hasSeenBoost) {
    hasSeenBoostModal.value = true
    stepper.boost = true
    return
  }

  if (!hasSeenBoostModal.value) {
    stepper.form = true
  }
}

const isInstagram = computed(() => {
  const ua = navigator.userAgent || navigator.vendor
  return ua.indexOf('Instagram') > -1
})

const redirectToBoostLink = () => {
  if (props.useLocalStorageAvailability === true) {
    const localStorageKey = `hasParticipated-${props.contestGame.id}-${props.contestGame.stores[0].storeId}-${props.contestGame.contestGameBoostType.slug}`
    localStorage.setItem(localStorageKey, 'true')
  }

  if (isInstagram.value) {
    if (redirectUrl.value) {
      window.location.href = redirectUrl.value
    }
  } else {
    if (redirectUrl.value) {
      window.open(redirectUrl.value, '_blank')
    }
  }
}

const handleBoost = () => {
  boostStep.value = 2
  hasSeenBoostModal.value = true
  redirectToBoostLink()
}

const updateLocalStorage = () => {
  if (props.useLocalStorageAvailability === true) {
    localStorage.setItem(`hasParticipated-${props.contestGame.id}`, 'true')
    localStorage.setItem(
      `hasParticipated-${props.contestGame.id}-${props.contestGame.stores[0].storeId}-${props.contestGame.contestGameBoostType.slug}`,
      'true',
    )
  }
}

const handleError = (error: {
  response: {
    status: number
    data: {
      errors: {
        phone_number: boolean
        email: boolean
      }
    }
  }
}) => {
  if (error?.response?.status === 409 || error?.response?.status === 500) {
    errorText.value = error.response as unknown as ErrorResponse
    errorModal.value = true
  } else {
    if (error?.response?.data?.errors?.phone_number) apiError.value.phoneNumber = true
    if (error?.response?.data?.errors?.email) apiError.value.email = true
  }
}

const createParticipant = async () => {
  const { data } = await props.clientApi.client.contestGamesParticipants.create(
    props.id,
    props.storeSlug,
    {
      phoneNumber: userForm.value.phoneNumber,
      country: userForm.value.countryCode ?? 'FR',
      email: userForm.value.email,
      firstName: userForm.value.firstName,
      hasOptin: userForm.value.hasOptin,
    },
  )

  if (props.contestGame.mechanic === 'fortuneWheel') {
    const nullReward = props.contestGameRewards.find((x) => !x.type)
    props.setContestReward(data.contestGameReward ?? nullReward)
  } else {
    props.setContestReward(data.contestGameReward ?? null)
  }
}

const handleBoostModal = () => {
  if (props.useLocalStorageAvailability === true) {
    showBoostModal()
  } else {
    stepper.form = true
  }
}

const handleBoostDrawer = () => {
  hasSeenBoostModal.value = true
  stepper.form = false
  stepper.boost = true
}

const nextStep = () => {
  stepper.intro = false

  if (props.contestGame.isClickRequiredToPlay) {
    handleBoostModal()
  } else {
    handleBoostDrawer()
  }
}

const nextStepForm = async () => {
  userFormError.value = ''
  isUserFormSubmitLoading.value = true

  try {
    await createParticipant()

    updateLocalStorage()
    stepper.boost = false
    launchGame.value = true
  } catch (error) {
    handleError(error)
  } finally {
    isUserFormSubmitLoading.value = false
  }
}

const launchFireWorks = ref(false)
const startRewardAnimation = ref(false)
const onCompleted = () => {
  launchFireWorks.value = true

  setTimeout(() => {
    startRewardAnimation.value = true
  }, 500)
}

onMounted(async () => {
  isContestLoaded.value = false
})
</script>

<template>
  <section
    class="mx-auto h-screen overflow-hidden lg:max-w-4xl"
    :style="{
      backgroundColor: contestColor?.backgroundColor,
      color: contestColor?.textColor,
    }"
  >
    <slot name="animation" :launch-fire-works="launchFireWorks" />

    <ContestGame
      v-model="launchGame"
      :contest-color="contestColor"
      :contest-game-rewards="contestGameRewards"
      :contest-reward="contestReward"
      :cover-image="contestGame.coverImageUrl"
      :logo="logo"
      :mechanic="contestGame.mechanic"
      :merchant="merchant"
      :contest-game="contestGame"
      :web-app-url="appUrl"
      @completed="onCompleted"
      @loading="loading = true"
    />

    <div>
      <IntroDrawer
        v-model="stepper.intro"
        :contest-color="contestColor"
        :contest-game-rewards="contestGameRewards"
        :contest-game="contestGame"
        @on-submit="nextStep"
      />
      <UserFormDrawer
        v-model="stepper.boost"
        v-model:userForm="userForm"
        v-model:apiError="apiError"
        :contest-game="contestGame"
        :contest-color="contestColor"
        :is-processing="isUserFormSubmitLoading"
        :on-submit="nextStepForm"
      />
      <BoostDrawer
        v-model="stepper.form"
        v-model:boost-step="boostStep"
        :contest-game="contestGame"
        :contest-color="contestColor"
        @onSubmit="handleBoost"
        @onSeeBoostDrawer="handleBoostDrawer"
      />

      <slot name="error-modal" :error-modal="errorModal" :error-text="errorText" :locale="locale" />
    </div>

    <slot name="reward" :start-reward-animation="startRewardAnimation" :app-url="appUrl" />

    <slot v-if="isContestLoaded" name="loader" />
  </section>
</template>
